// Small shared UI kit — brand tokens from tailwind.config.js (paper/ink/line/
// accent; Playfair display, DM Sans, DM Mono; sharp corners, borders not shadows).
import { useEffect, useRef } from 'react';

export function Screen({ children }) {
  return (
    <main className="min-h-full flex flex-col items-center px-6 py-10">
      <div className="w-full max-w-md">{children}</div>
    </main>
  );
}

export function Card({ children, className = '' }) {
  return <div className={`border border-line p-8 ${className}`}>{children}</div>;
}

export function Eyebrow({ children }) {
  return <p className="font-mono text-xs uppercase tracking-widest text-accent">{children}</p>;
}

export function Title({ children, className = '' }) {
  return <h1 className={`font-display text-4xl leading-tight ${className}`}>{children}</h1>;
}

export function PrivacyLine() {
  // Locked copy (Matty, 13 Aug) — do not edit.
  return (
    <p className="mt-6 font-sans text-sm text-ink/60">
      We only ever show free or busy. Never what you're doing.
    </p>
  );
}

export function Button({ children, onClick, disabled, variant = 'primary', type = 'button' }) {
  const base = 'w-full font-mono text-sm uppercase tracking-wide px-5 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const styles = variant === 'primary'
    ? 'bg-ink text-paper hover:bg-accent'
    : 'border border-line text-ink hover:bg-ink hover:text-paper';
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}

// A LOAD FAILURE — deliberately distinct from an empty result. Home's empty state
// once rendered on a failed load and silently swallowed a CORS error for two review
// rounds; a failure must never look like "you have nothing yet."
export function ErrorState({ message, onRetry }) {
  return (
    <Card className="mt-4">
      <p className="font-mono text-xs uppercase tracking-widest text-red-700">Something went wrong</p>
      <p className="mt-3 font-sans text-base text-ink/80">{message}</p>
      {onRetry && (
        <div className="mt-6">
          <Button onClick={onRetry} variant="secondary">Try again</Button>
        </div>
      )}
    </Card>
  );
}

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12" role="status" aria-label={label}>
      <div className="h-6 w-6 animate-spin border-2 border-line border-t-transparent" />
      <p className="font-mono text-xs text-ink/50">{label}</p>
    </div>
  );
}

// First typed-input primitive in the when kit (every prior flow was OAuth-redirect).
// Label + input in the brand style — sharp borders, mono, no shadow. `hint` renders
// a small helper line below.
export function TextInput({ id, label, hint, type = 'text', value, onChange, placeholder, autoComplete, disabled, required }) {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block font-mono text-xs uppercase tracking-widest text-ink/60">{label}</label>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        className="mt-2 w-full border border-line bg-paper px-4 py-3 font-sans text-base text-ink placeholder:text-ink/30 focus:border-accent focus:outline-none disabled:opacity-50"
      />
      {hint && <p className="mt-2 font-mono text-xs text-ink/50">{hint}</p>}
    </div>
  );
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Meeting-duration options for the overlap screen (Section 1 of the durations
// addendum). Fixed set, no free entry; minutes are the API's duration_minutes.
export const DURATIONS = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hr' },
  { minutes: 90, label: '90 min' },
  { minutes: 120, label: '2 hr' },
];

// Segmented duration control — mirrors DayStrip's button idiom (accent fill on
// select). `selected` / `onSelect` speak in minutes.
export function DurationPicker({ selected, onSelect }) {
  return (
    <div role="radiogroup" aria-label="Meeting length" className="flex gap-2">
      {DURATIONS.map((d) => {
        const isSelected = d.minutes === selected;
        return (
          <button
            key={d.minutes}
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(d.minutes)}
            className={`flex-1 border px-3 py-2 font-mono text-xs uppercase tracking-wide transition-colors ${
              isSelected ? 'border-accent bg-accent text-paper' : 'border-line text-ink hover:bg-ink hover:text-paper'
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

// Horizontal day strip — the primary date control. `days` is an ordered list of
// Dates (already bounded to [today, horizon] by the caller). Thumb-scrollable;
// the selected day fills with `accent`. No freeness marking yet (Phase 2).
export function DayStrip({ days, selected, onSelect }) {
  const selectedRef = useRef(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [selected]);
  return (
    <div className="-mx-6 overflow-x-auto px-6" role="listbox" aria-label="Pick a day">
      <div className="flex gap-2">
        {days.map((d) => {
          const isSelected = sameDay(d, selected);
          return (
            <button
              key={d.toISOString()}
              ref={isSelected ? selectedRef : null}
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(d)}
              className={`flex shrink-0 flex-col items-center border px-3 py-2 font-mono text-xs leading-tight transition-colors ${
                isSelected ? 'border-accent bg-accent text-paper' : 'border-line text-ink hover:bg-ink hover:text-paper'
              }`}
            >
              <span className="uppercase tracking-widest opacity-70">
                {d.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span className="mt-1 text-base">{d.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compact month grid for far jumps. Pure of any clock — the caller passes the
// visible `month` (any Date in it), the `selected` day, and `isSelectable(date)`
// which enforces the [today, horizon] bounds. Unselectable days are dimmed and
// disabled, never hidden. Week starts Monday.
export function MonthGrid({ month, selected, onSelect, isSelectable, onPrevMonth, onNextMonth, canPrev, canNext }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leadBlanks = (first.getDay() + 6) % 7; // Mon=0 … Sun=6
  const cells = [
    ...Array.from({ length: leadBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];

  return (
    <div className="border border-line p-4">
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevMonth}
          disabled={!canPrev}
          className="font-mono text-xs text-ink/60 hover:text-ink disabled:opacity-30 disabled:hover:text-ink/60"
          aria-label="Previous month"
        >
          ←
        </button>
        <p className="font-mono text-xs uppercase tracking-widest">
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </p>
        <button
          onClick={onNextMonth}
          disabled={!canNext}
          className="font-mono text-xs text-ink/60 hover:text-ink disabled:opacity-30 disabled:hover:text-ink/60"
          aria-label="Next month"
        >
           →
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="font-mono text-[10px] uppercase tracking-widest text-ink/40">{d}</span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const selectable = isSelectable(d);
          const isSelected = sameDay(d, selected);
          return (
            <button
              key={d.toISOString()}
              onClick={() => selectable && onSelect(d)}
              disabled={!selectable}
              aria-selected={isSelected}
              className={`aspect-square font-mono text-sm transition-colors ${
                isSelected ? 'bg-accent text-paper' : selectable ? 'text-ink hover:bg-ink hover:text-paper' : 'text-ink/20'
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
